import * as React from 'react';
import { Link } from 'react-router-dom';
import { useXyqarAuth } from '@xyqar/shared-auth';
import { apiGet } from '../lib/api';
import { PageTitle, Card, EmptyState, Section, Kpi, Pill, fmt } from '../components/ui-kit';

interface Mission { id: string; title: string; client: string; status: string; }

const CLOSED_STATUSES = new Set(['completed', 'closed', 'cancelled']);

export default function Missions() {
  const { getToken } = useXyqarAuth();
  const [items, setItems] = React.useState<Mission[] | null>(null);
  React.useEffect(() => {
    (async () => {
      try { const tok = await getToken().catch(() => ''); const j = await apiGet<{ missions: Mission[] }>('/api/v1/expert/missions', tok); setItems(j.missions ?? []); }
      catch { setItems([]); }
    })();
  }, [getToken]);

  const liveItems = (items ?? []).filter((mission) => !CLOSED_STATUSES.has((mission.status ?? '').toLowerCase()));
  const closedItems = (items ?? []).filter((mission) => CLOSED_STATUSES.has((mission.status ?? '').toLowerCase()));
  const clients = new Set((items ?? []).map((mission) => mission.client).filter(Boolean)).size;

  return (
    <div>
      <PageTitle kicker="Engagements" title="Missions" />

      <Section title="At a glance" hint="Every mission card opens the CRA workspace for time entry and weekly follow-up.">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Kpi accent="green" label="Live missions" value={fmt(liveItems.length)} />
          <Kpi label="Closed" value={fmt(closedItems.length)} />
          <Kpi accent="blue" label="Clients" value={fmt(clients)} />
          <Kpi label="Total mission count" value={fmt(items?.length)} />
        </div>
      </Section>

      <Section title="Operating cadence">
        <div className="grid gap-4 lg:grid-cols-[1.35fr,0.65fr]">
          <Card>
            <p className="text-sm text-white">CRA is the control point for every assignment.</p>
            <p className="text-xs text-white/60 mt-2 leading-relaxed">
              Open the mission, log time in CRA, and keep the mission state aligned with the actual engagement. Closed missions stay visible below for audit continuity.
            </p>
          </Card>
          <Card>
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-mono">Checklist</p>
            <div className="mt-3 space-y-2 text-xs text-white/70">
              <p>Open active missions weekly.</p>
              <p>Submit CRA before invoicing closes.</p>
              <p>Use archived cards as proof trail only.</p>
            </div>
          </Card>
        </div>
      </Section>

      <Section title="Mission roster" hint="Direct access to each mission workspace.">
        {items === null && <Card>Loading…</Card>}
        {items && items.length === 0 && <EmptyState title="No missions yet" hint="Accepted engagements will appear here automatically." />}
        {items && items.length > 0 && (
          <div className="flex flex-col gap-3">
            {items.map((m) => {
              const live = !CLOSED_STATUSES.has((m.status ?? '').toLowerCase());
              return (
                <Link to={`/missions/${m.id}/cra`} key={m.id}>
                  <Card className="hover:border-white/30 transition cursor-pointer">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="text-sm font-bold">{m.title}</p>
                        <p className="text-xs text-white/40 mt-1 font-mono">{m.client} · {m.id}</p>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <Pill tone={live ? 'live' : 'mute'}>{m.status}</Pill>
                        <span className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-mono">Open CRA</span>
                      </div>
                    </div>
                  </Card>
                </Link>
              );
            })}
          </div>
        )}
      </Section>
    </div>
  );
}
